import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuizIcon from '@mui/icons-material/Quiz';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCourses } from '../../store/thunks/coursesThunks';
import QuizCard from '../quizzes/QuizCard';
import { fetchQuestionsByCourseId } from '../../services/firestore/questionService';

const QuizSection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { courses } = useAppSelector((state) => state.courses);
  const [featuredQuizzes, setFeaturedQuizzes] = React.useState<
    Array<{ course: any; questionCount: number }>
  >([]);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    const loadFeaturedQuizzes = async () => {
      setLoading(true);
      const coursesWithQuizzes = await Promise.all(
        courses.slice(0, 3).map(async (course) => {
          try {
            const questions = await fetchQuestionsByCourseId(course.id);
            return {
              course,
              questionCount: questions.length,
            };
          } catch (error) {
            return null;
          }
        })
      );
      setFeaturedQuizzes(coursesWithQuizzes.filter((item) => item !== null && item.questionCount > 0) as Array<{ course: any; questionCount: number }>);
      setLoading(false);
    };

    if (courses.length > 0) {
      loadFeaturedQuizzes();
    } else {
      setLoading(false);
    }
  }, [courses]);

  if (loading || featuredQuizzes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(15, 25, 45, 1) 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
      <Fade in timeout={1000}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              mb: 3,
            }}
          >
            <QuizIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Box>
          <Typography
            variant="h3"
            component="h2"
            sx={{ mb: 2, fontWeight: 700 }}
          >
            Featured Quizzes
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            Test your knowledge and track your progress with our interactive quizzes
          </Typography>
        </Box>
      </Fade>
      <Grid container spacing={4}>
        {featuredQuizzes.map(({ course, questionCount }, index) => (
          <Grid item xs={12} md={4} key={course.id}>
            <QuizCard course={course} index={index} questionCount={questionCount} />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button
          variant="outlined"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/quizzes')}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            borderColor: 'rgba(99, 102, 241, 0.5)',
            '&:hover': {
              borderColor: '#6366f1',
              background: 'rgba(99, 102, 241, 0.1)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          View All Quizzes
        </Button>
      </Box>
      </Container>
    </Box>
  );
};

export default QuizSection;

